/**
 * QuestionScreen — the generic, data-driven renderer.
 *
 * Renders one CustomerQuestionScreen (one or more grouped questions) using the
 * shared patterns: SINGLE/MULTI as .pb-option lists, NUMBER/TEXT as fields.
 * A composite screen (group_code) shows its questions stacked under one heading.
 *
 * Records raw answers into the DataFlow session (option select is immediate;
 * number/text is debounced) and shows the live scope list in the sticky rail.
 *
 * "I'm not sure" options use the same .pb-option treatment as any other answer.
 */
import { useState } from 'react';
import type { CustomerQuestion, CustomerQuestionScreen, RawAnswer } from '../lib/api';
import { useDataFlow } from '../lib/dataFlowSession';
import { useDebouncedCallback } from '../lib/useDebouncedCallback';
import { Progress } from './Progress';
import { ScopeList } from './ScopeList';

interface QuestionScreenProps {
  screen: CustomerQuestionScreen;
  step: number;
  totalSteps: number;
  onNext: () => void;
  onBack?: () => void;
  /** Which section the customer is in, e.g. "Interior paint". Shown above the prompt. */
  sectionLabel?: string;
  /** Save & exit — persist and return to the dashboard. */
  onSaveExit?: () => void;
  /** Cancel — discard this draft and return to the dashboard. */
  onCancel?: () => void;
}

function Check(): JSX.Element {
  return (
    <svg width="12" height="12" viewBox="0 0 18 18" fill="none" aria-hidden="true">
      <path d="M3 9.5l4 4 8-9" stroke="currentColor" strokeWidth="2.6" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

/** SINGLE / MULTI option list. */
function OptionQuestion({ question }: { question: CustomerQuestion }): JSX.Element {
  const flow = useDataFlow();
  const existing = flow.getAnswer(question.code);
  const selected = new Set(existing?.selectedOptionCodes ?? []);
  const multi = question.inputType === 'MULTI';

  function choose(code: string): void {
    let next: string[];
    if (multi) {
      next = selected.has(code) ? [...selected].filter((c) => c !== code) : [...selected, code];
    } else {
      next = [code];
    }
    // Preserve any typed per-option numbers (e.g. whole-house room count).
    const answer: RawAnswer = {
      questionCode: question.code,
      selectedOptionCodes: next,
      ...(existing?.numberByOption ? { numberByOption: existing.numberByOption } : {}),
    };
    flow.setAnswer(answer);
  }

  function setOptionNumber(code: string, raw: string): void {
    const n = Number(raw);
    const numberByOption = { ...(existing?.numberByOption ?? {}) };
    if (raw === '' || Number.isNaN(n)) delete numberByOption[code];
    else numberByOption[code] = n;
    flow.setAnswer({
      questionCode: question.code,
      selectedOptionCodes: [...selected],
      numberByOption,
    });
  }

  return (
    <div className="options" role={multi ? 'group' : 'radiogroup'} aria-label={question.prompt}>
      {question.options.map((opt) => {
        const isSel = selected.has(opt.code);
        // A requiresNumber option reveals a number input ONLY on SINGLE questions
        // (e.g. paint "Whole house" → how many rooms). On MULTI checklist screens
        // we don't ask a per-item count — most customers can't give an exact
        // number, so each checked item counts as its default (value_number) and
        // quantities are settled at the follow-up/site visit.
        const showNumber = isSel && opt.requiresNumber && !multi;
        const numberValue =
          existing?.numberByOption?.[opt.code] ?? (opt.defaultNumber ?? undefined);
        return (
          <div key={opt.code}>
            <button
              type="button"
              className="pb-option"
              role={multi ? 'checkbox' : 'radio'}
              aria-checked={isSel}
              onClick={() => choose(opt.code)}
            >
              <span
                className="pb-option__mark"
                style={isSel ? { background: 'var(--pb-chalk)', borderColor: 'var(--pb-chalk)', color: '#fff' } : undefined}
              >
                {isSel ? <Check /> : null}
              </span>
              <span style={{ flexGrow: 1 }}>
                <span className="pb-option__label">{opt.label}</span>
                {opt.sublabel ? <span className="pb-option__sub">{opt.sublabel}</span> : null}
              </span>
            </button>
            {showNumber ? (
              <div className="subfield" style={{ marginTop: 'var(--pb-2)', marginLeft: 'var(--pb-5)' }}>
                <label className="pb-label" htmlFor={`${question.code}-${opt.code}-num`}>
                  How many?
                </label>
                <input
                  id={`${question.code}-${opt.code}-num`}
                  className="pb-field"
                  inputMode="numeric"
                  defaultValue={numberValue != null ? String(numberValue) : ''}
                  onChange={(e) => setOptionNumber(opt.code, e.target.value)}
                  aria-label="How many"
                />
              </div>
            ) : null}
          </div>
        );
      })}
    </div>
  );
}

/** NUMBER field — sets a session variable or an answer number, debounced. */
function NumberQuestion({ question }: { question: CustomerQuestion }): JSX.Element {
  const flow = useDataFlow();
  const [value, setValue] = useState<string>(() => {
    const n = flow.getAnswer(question.code)?.numberValue;
    return n != null ? String(n) : '';
  });
  const commit = useDebouncedCallback((raw: string) => {
    const n = Number(raw);
    if (!Number.isNaN(n)) flow.setAnswer({ questionCode: question.code, numberValue: n });
  }, 400);

  return (
    <div className="subfield">
      <label className="pb-label" htmlFor={question.code}>
        {question.prompt}
      </label>
      <input
        id={question.code}
        className="pb-field"
        inputMode="numeric"
        value={value}
        onChange={(e) => {
          setValue(e.target.value);
          commit(e.target.value);
        }}
      />
      {question.helper ? <span className="estimate__hint">{question.helper}</span> : null}
    </div>
  );
}

/** TEXT field — free text, debounced (no pricing consequence). */
function TextQuestion({ question }: { question: CustomerQuestion }): JSX.Element {
  const flow = useDataFlow();
  const [value, setValue] = useState<string>(() => flow.getAnswer(question.code)?.textValue ?? '');
  const commit = useDebouncedCallback((raw: string) => {
    flow.setAnswer({ questionCode: question.code, textValue: raw });
  }, 400);

  return (
    <div className="subfield">
      <label className="pb-label" htmlFor={question.code}>
        {question.prompt}
      </label>
      <input
        id={question.code}
        className="pb-field"
        value={value}
        onChange={(e) => {
          setValue(e.target.value);
          commit(e.target.value);
        }}
      />
      {question.helper ? <span className="estimate__hint">{question.helper}</span> : null}
    </div>
  );
}

/** Multi-select questions get a "(choose all that apply)" cue after the prompt. */
const MULTI_HINT = ' (choose all that apply)';
function isMulti(q: CustomerQuestion): boolean {
  return q.inputType === 'MULTI';
}

function QuestionBody({ question, showPrompt }: { question: CustomerQuestion; showPrompt: boolean }): JSX.Element {
  const isField = question.inputType === 'NUMBER' || question.inputType === 'TEXT';
  return (
    <div style={{ marginBottom: 'var(--pb-5)' }}>
      {/* For grouped composites, each sub-question shows its own small prompt.
          NUMBER/TEXT render the prompt via their own label. */}
      {showPrompt && !isField ? (
        <h2 style={{ fontSize: 'var(--pb-size-h4)', marginBottom: 'var(--pb-3)' }}>
          {question.prompt}
          {isMulti(question) ? <span className="q-head__helper">{MULTI_HINT}</span> : null}
        </h2>
      ) : null}
      {question.inputType === 'NUMBER' ? (
        <NumberQuestion question={question} />
      ) : question.inputType === 'TEXT' ? (
        <TextQuestion question={question} />
      ) : (
        <OptionQuestion question={question} />
      )}
    </div>
  );
}

export function QuestionScreen({
  screen,
  step,
  totalSteps,
  onNext,
  onBack,
  sectionLabel,
  onSaveExit,
  onCancel,
}: QuestionScreenProps): JSX.Element {
  const flow = useDataFlow();
  const isComposite = screen.questions.length > 1;
  // On a single-question screen the prompt is the <h1>; if that question is
  // multi-select, append the hint here (composites handle it per sub-question).
  const singleMultiHint = !isComposite && screen.questions[0] && isMulti(screen.questions[0]);

  return (
    <div className="flow">
      <div className="flow__main">
        <Progress step={step} total={totalSteps} onBack={onBack} />

        <div className="q-head">
          {sectionLabel ? (
            <div className="progress__count" style={{ marginBottom: 'var(--pb-2)' }}>
              {sectionLabel.toUpperCase()}
            </div>
          ) : null}
          <h1>
            {screen.prompt}
            {singleMultiHint ? <span className="q-head__helper">{MULTI_HINT}</span> : null}
          </h1>
          {screen.helper ? <p className="q-head__helper">{screen.helper}</p> : null}
        </div>

        {screen.questions.map((q) => (
          <QuestionBody key={q.code} question={q} showPrompt={isComposite} />
        ))}

        <div className="q-actions">
          <button type="button" className="pb-btn pb-btn--next pb-btn--lg" onClick={onNext}>
            Next
          </button>
        </div>

        {onSaveExit || onCancel ? (
          <div className="flow-exit">
            {onSaveExit ? (
              <button type="button" className="flow-exit__link" onClick={onSaveExit}>
                Save &amp; exit
              </button>
            ) : null}
            {onCancel ? (
              <button type="button" className="flow-exit__link flow-exit__link--danger" onClick={onCancel}>
                Cancel this project
              </button>
            ) : null}
          </div>
        ) : null}
      </div>

      <aside className="flow__rail">
        <ScopeList items={flow.scopeItems} />
      </aside>
    </div>
  );
}
